import CloseIcon from "@mui/icons-material/Close";
import DoneIcon from "@mui/icons-material/Done";
import SaveIcon from "@mui/icons-material/Save";
import SearchIcon from "@mui/icons-material/Search";
import { debounce, styled } from "@mui/material";
import Avatar from "@mui/material/Avatar";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import List from "@mui/material/List";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemText from "@mui/material/ListItemText";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import Loader from "_shared/_views/loading/loader";
import LoadingButton from "_shared/_views/loading/loading-button";
import IndividualAvatar from "marketplace/_views/avatars/individual-avatar";
import { useInviteSubscriberToForumMutation } from "messaging/_api/messaging-api";
import { getMessagingErrorMessage, getMessagingFieldErrors } from "messaging/_api/messaging-error-message";
import Forum from "messaging/forums/forum";
import { enqueueSnackbar } from "notistack";
import React from "react";
import UserInformation from "users/information/user-information";
import { useGetUserNetworkConnectionsQuery } from "users/networking/connections/_api/user-network-connection-api";

const AddSubscriberContainer = styled("div")(({ theme }) => ({
  minWidth: "22rem",
  padding: theme.spacing(2),
  textAlign: "center",
}));
const SearchField = styled(TextField)(({ theme }) => ({
  width: "100%",
  padding: theme.spacing(1),
})) as typeof TextField;
const ListContainer = styled(List)(() => ({
  maxHeight: "20rem",
  overflowX: "hidden",
  overflowY: "auto",
}));
const SelectedIcon = styled(Avatar)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
}));
const StyledListItemButton = styled(ListItemButton)(({ theme }) => ({
  padding: theme.spacing(1),
}));
const MessageInput = styled(TextField)(({ theme }) => ({
  marginBottom: theme.spacing(1.6),
  marginTop: theme.spacing(1.6),
}));
const ButtonContainer = styled("div")(({ theme }) => ({
  display: "grid",
  gap: theme.spacing(1),
  gridTemplateColumns: "1fr 1fr",
  width: "100%",
}));

type AddSubscriberProps = {
  forum: Forum;
  onClosed: () => void;
};

export default function AddSubscriber(props: Readonly<AddSubscriberProps>) {
  const { onClosed, forum } = props;
  
  const [isSaving, setIsSaving] = React.useState(false);
  const [selectedSubscribers, setSelectedSubscribers] = React.useState<UserInformation[]>([]);
  const [searchTerm, setSearchTerm] = React.useState<string>("");
  const [customMessage, setCustomMessage] = React.useState<string>("");
  const [fieldError, setFieldError] = React.useState<string | undefined>();

  const [inviteSubscriberToForum] = useInviteSubscriberToForumMutation();

  const networkConnectionQuery = useGetUserNetworkConnectionsQuery();

  const networkConnections = React.useMemo(() => {
    if (!networkConnectionQuery.data) return [];
    return UserInformation.fromJSONArray(networkConnectionQuery.data);
  }, [networkConnectionQuery.data]);

  function handleSubscriberSelected(
    event: React.MouseEvent<HTMLDivElement, MouseEvent>,
    subscriber: UserInformation
  ): void {
    event.stopPropagation();

    const alreadySelected = selectedSubscribers.some((r) => r.id?.isEqualTo(subscriber.id));
    if (alreadySelected) {
      setSelectedSubscribers((prevSub) => prevSub.filter((r) => r.id?.value !== subscriber.id?.value));
    } else {
      setSelectedSubscribers((prevSub) => [...prevSub, subscriber]);
    }
  }

  function search(value: string) {
    setSearchTerm(value);
    if (value === "") {
      return;
    }
    debouncedSearch(value);
  }

  const debouncedSearch = debounce((_value) => {}, 100);

  const canAddSubscribers = (
    (networkConnections.length ?? 0) > 0 
    && (forum.subscribers?.length ?? 0) - 1 < (networkConnections.length ?? 0)
  );

  async function saveChanges(): Promise<void> {
    if (!forum.id) {
      setFieldError("A valid conversation is required.");
      return;
    }
    if (selectedSubscribers.length < 1) {
      setFieldError("Select at least one person to invite.");
      return;
    }
    if (customMessage.trim().length === 0 && customMessage.length > 0) {
      setFieldError("Invitation message cannot be blank.");
      return;
    }
    setFieldError(undefined);
    setIsSaving(true);
    try {
      for (const subscriber of selectedSubscribers) {
        await inviteSubscriberToForum({
          forumId: forum.id.value,
          inviteeId: subscriber.id,
          invitationMessage: customMessage,
        }).unwrap();
      }
      enqueueSnackbar("Forum subscribers saved", { variant: "success" });
      onClosed();
    } catch (error) {
      const fieldErrors = getMessagingFieldErrors(error);
      setFieldError(fieldErrors.inviteeId ?? fieldErrors.invitationMessage);
      enqueueSnackbar(getMessagingErrorMessage(error, "Failed to save forum subscriber changes"), { variant: "error" });
    } finally {
      setIsSaving(false);
    }
  }

  return (
    <React.Fragment>
      {networkConnectionQuery.isLoading && <Loader size={50} />}
      {!networkConnectionQuery.isLoading && (
        <AddSubscriberContainer>
          {(networkConnections.length ?? 0) > 10 && (
            <SearchField
              placeholder="Search Connections"
              slotProps={{
                input: {
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <IconButton
                      size="small"
                      onClick={async () => {
                        setSearchTerm("");
                        search("");
                      }}
                    >
                      <CloseIcon />
                    </IconButton>
                  ),
                },
              }}
              value={searchTerm}
              onChange={(event) => search(event.target.value)}
            />
          )}
          {!canAddSubscribers && <Typography>All connections are already subscribed</Typography>}
          <ListContainer dense>
            {canAddSubscribers &&
              networkConnections.map((connection: UserInformation) => {
                const isAlreadySubscribed = Boolean(forum.subscribers?.some((r) => r.userId.isEqualTo(connection.id)));
                if (isAlreadySubscribed) return null;

                const isSelected = selectedSubscribers?.some((r) => r.id.isEqualTo(connection.id));
                return (
                  <StyledListItemButton
                    disableGutters
                    key={connection.id?.value}
                    selected={isSelected}
                    onClick={(event) => handleSubscriberSelected(event, connection)}
                  >
                    <ListItemAvatar>
                      {isSelected ? (
                        <SelectedIcon>
                          <DoneIcon />
                        </SelectedIcon>
                      ) : (
                        renderSubscriberAvatar()
                      )}
                    </ListItemAvatar>
                    <ListItemText primary={connection.name?.toString()} secondary={connection.email?.value} />
                  </StyledListItemButton>
                );

                function renderSubscriberAvatar(): React.ReactNode {
                  return connection.avatarId ? (
                    <IndividualAvatar
                      individual={connection.toIndividual()}
                    />
                  ) : (
                    <Avatar>{connection.name?.toString().charAt(0)}</Avatar>
                  );
                }
              })}
          </ListContainer>
          {selectedSubscribers.length > 0 && (
            <MessageInput
              multiline
              variant="outlined"
              minRows={3}
              maxRows={6}
              fullWidth
              label="Message (optional)"
              value={customMessage}
              onChange={(event) => setCustomMessage(event.target.value)}
              error={Boolean(fieldError)}
              helperText={fieldError}
            />
          )}
          {fieldError && selectedSubscribers.length === 0 && <Typography color="error">{fieldError}</Typography>}
          <ButtonContainer>
            <LoadingButton
              variant="contained"
              color="primary"
              startIcon={<SaveIcon />}
              loading={isSaving}
              disabled={selectedSubscribers.length < 1 || isSaving}
              onClick={saveChanges}
            >
              Save
            </LoadingButton>
            <Button variant="contained" color="primary" onClick={onClosed}>
              Cancel
            </Button>
          </ButtonContainer>
        </AddSubscriberContainer>
      )}
    </React.Fragment>
  );
}
